/**
 * Breadcrumb — context-aware "you are here" trail rendered globally
 * above every routed page in AppShell.
 *
 * Auto-renders when:
 *   - A portfolio is selected (always shows portfolio name as the root)
 *   - For project routes: also shows the selected project name
 *   - For sub-routes within project context: also shows the current page
 *
 * Returns null when there's nothing meaningful to render (Context
 * Selector, Project Detail itself, etc.) so the slot stays clean.
 *
 * Route → page-name mapping is centralized below; add an entry for
 * any new subpage that should show in the trail.
 */

import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { cn } from '@/lib/utils'
import { getFinancePageLabel } from '@/lib/projectClassification'
import { TIMESHEET_ADMIN_GROUPS } from '@/modules/timesheet/nav/adminGroups'
import { CONTEXT_ROOTS, resolveGenericPageLabel, resolveGenericPageParent } from './breadcrumbConfig'

const PROJECT_PAGE_NAMES: Record<string, string> = {
  '/project-overview': 'Project Overview',
  '/hierarchy':        'WBS Hierarchy',
  '/hierarchy/linkages': 'Hierarchy Linkages',
  '/gantt':            'Scheduling',
  '/finance':          'Baseline Management',
  '/forecasts':        'Forecast Management',
  '/evm':              'Progress & EV',
  '/risks':            'RAID Log',
  '/resource-consumption': 'Resources',
  '/team':             'Team Manager',
  '/reports':          'Project Reports',
  '/contracts':        'Contracts',
}

const PORTFOLIO_PAGE_NAMES: Record<string, string> = {
  '/projects':           'Active Projects',
  '/budget-releases':    'Portfolio Budgeting',
  '/scenario-planning':  'Scenario Planning',
  '/priorities':         'Priorities & KPIs',
  '/roi':                'ROI',
  '/analysis':           'Analysis & Attributes',
  '/workflows':          'Workflow',
  '/risks':              'Risks, Issues & Opps',
  '/milestone-analyzer': 'Milestone Analyzer',
  '/programs/review':    'Program Review',
  '/project-requests':   'Project Requests',
  '/resource-consumption': 'Resource Consumption',
}

// ── Timesheet breadcrumbs (ProjectTasks: per-page crumbs in the timesheet) ──
// The timesheet module mounts at /timesheet/* inside AppShell, so it shares
// this breadcrumb slot — but until now the component recognized no /timesheet
// paths and rendered nothing. Labels are sourced from the same nav data the
// sidebar + admin hub use, so they stay in sync automatically.
//
// Worker + top-level surfaces (the sidebar pill nav + hub shortcuts). The
// ~99 admin surfaces come from TIMESHEET_ADMIN_GROUPS below.
const TIMESHEET_PAGE_NAMES: Record<string, string> = {
  '/timesheet/my':                   'My Timesheet',
  '/timesheet/my-utilization':       'My Utilization',
  '/timesheet/pto':                  'PTO Balance',
  '/timesheet/my-pto-calendar':      'My PTO Calendar',
  '/timesheet/my-compliance':        'My Compliance',
  '/timesheet/my-data-requests':     'My Data Requests',
  '/timesheet/wand':                 'Clock In / Clock Out',
  '/timesheet/my-manufacturing-day': 'My Manufacturing Day',
  '/timesheet/my-week':              'My Manufacturing Week',
  '/timesheet/floor-activity':       'Floor Activity',
  '/timesheet/floor-check':          'Floor Check',
  '/timesheet/exceptions':           'Exceptions Queue',
  '/timesheet/call-out-roster':      'Call-Out Roster',
  '/timesheet/approvals':            'Approvals Queue',
  '/timesheet/sub-approvals':        'Subcontractor Approvals',
  '/timesheet/subcontractors':       'Subcontractors',
  '/timesheet/reports':              'Reports',
  '/timesheet/settings':             'Settings',
  '/timesheet/admin':                'Admin & Reporting',
}

// Admin-group items: path → label, plus the set of paths that live UNDER the
// Admin & Reporting hub (so those crumbs read Timesheet › Admin & Reporting › X).
const TIMESHEET_ADMIN_PAGE_NAMES: Record<string, string> = {}
const TIMESHEET_ADMIN_PATHS = new Set<string>()
for (const group of TIMESHEET_ADMIN_GROUPS) {
  for (const item of group.items) {
    TIMESHEET_ADMIN_PAGE_NAMES[item.path] = item.label
    TIMESHEET_ADMIN_PATHS.add(item.path)
  }
}

// Title-case a raw path segment as a last-resort label for any timesheet page
// not in the maps above (e.g. dynamic detail routes), so no page is crumb-less.
function titleCaseSegment(seg: string): string {
  return seg.split('-').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

// Longest-prefix label lookup across both timesheet maps. Returns the matched
// label + whether the match is an admin-hub page.
function resolveTimesheetPage(path: string): { label: string; isAdmin: boolean } | null {
  let best = ''
  let label: string | undefined
  let isAdmin = false
  const consider = (prefix: string, name: string, admin: boolean, allowTie = false) => {
    const matches = path === prefix || path.startsWith(prefix + '/')
    const wins = prefix.length > best.length || (allowTie && prefix.length === best.length)
    if (matches && wins) {
      best = prefix; label = name; isAdmin = admin
    }
  }
  for (const [p, n] of Object.entries(TIMESHEET_PAGE_NAMES)) consider(p, n, false)
  // Admin map wins ties: pages listed in BOTH maps (Approvals Queue, Sub
  // Approvals, Floor Check, Reports, Settings, Subcontractors) must still get
  // the Admin & Reporting crumb so you can navigate back to the hub.
  for (const [p, n] of Object.entries(TIMESHEET_ADMIN_PAGE_NAMES)) consider(p, n, TIMESHEET_ADMIN_PATHS.has(p), true)
  if (label) return { label, isAdmin }
  // Fallback: title-case the first segment after /timesheet/.
  const seg = path.replace(/^\/timesheet\/?/, '').split('/')[0]
  return seg ? { label: titleCaseSegment(seg), isAdmin: false } : null
}

export function Breadcrumb() {
  const location = useLocation()
  const navigate = useNavigate()
  const portfolio = useAppStore(s => s.selectedPortfolio)
  const project = useAppStore(s => s.selectedProject)
  const ctx = useAppStore(s => s.managementContext)
  const setManagementContext = useAppStore(s => s.setManagementContext)

  const path = location.pathname

  // Build crumb trail from the current path
  const crumbs: Array<{ label: string; onClick?: () => void; current?: boolean }> = []

  // ── Timesheet context ──────────────────────────────────────────────────
  // The timesheet module is its own context (no selected portfolio), so it
  // runs BEFORE the portfolio gate below. Trail: Timesheet › [Admin &
  // Reporting] › <Page>. The bare /timesheet dashboard renders no trail.
  if (path === '/timesheet' || path.startsWith('/timesheet/')) {
    if (path === '/timesheet') return null // dashboard root — single crumb, no trail
    crumbs.push({ label: 'Timesheet', onClick: () => navigate('/timesheet') })
    const resolved = resolveTimesheetPage(path)
    if (resolved) {
      // Admin-hub pages get the intermediate Admin & Reporting crumb (unless
      // we're already on the hub itself).
      if (resolved.isAdmin && path !== '/timesheet/admin') {
        crumbs.push({ label: 'Admin & Reporting', onClick: () => navigate('/timesheet/admin') })
      }
      crumbs.push({ label: resolved.label, current: true })
    }
    if (crumbs.length < 2) return null
    return (
      <nav aria-label="Breadcrumb" className="flex items-center gap-0.5 text-[11px] leading-tight text-text-tertiary mb-2">
        {crumbs.map((c, i) => (
          <div key={i} className="flex items-center gap-0.5 min-w-0">
            {i > 0 && <ChevronRight size={10} className="text-text-tertiary/60 flex-shrink-0" />}
            {c.onClick ? (
              <button onClick={c.onClick} className="hover:text-brand-600 transition-colors truncate max-w-[140px] px-0.5" title={c.label}>
                {c.label}
              </button>
            ) : (
              <span className={cn('truncate max-w-[200px] px-0.5', c.current && 'text-text-primary font-medium')} title={c.label}>
                {c.label}
              </span>
            )}
          </div>
        ))}
      </nav>
    )
  }

  // ── Generic context breadcrumbs ────────────────────────────────────────
  // Every OTHER management context (organizational, personnel, contract,
  // supply-chain, configuration, my-work, workflow-management) gets a
  // `Context › Page` trail. Runs BEFORE the portfolio gate so these contexts
  // render even with no portfolio selected. Portfolio/project + timesheet keep
  // their bespoke trails above. Labels + roots live in breadcrumbConfig.
  const genericRoot = ctx ? CONTEXT_ROOTS[ctx] : undefined
  if (genericRoot && ctx && path !== '/' && path !== '/dashboard') {
    const pageLabel = resolveGenericPageLabel(path)
    const hasPage = !!pageLabel && pageLabel !== genericRoot.label
    const trail: Array<{ label: string; onClick?: () => void; current?: boolean }> = [
      {
        label: genericRoot.label,
        // Clickable back to the context home only when we're on a sub-page.
        onClick: hasPage
          ? () => {
              setManagementContext(ctx)
              navigate(genericRoot.path)
            }
          : undefined,
        current: !hasPage,
      },
    ]
    const parentLabel = hasPage ? resolveGenericPageParent(path) : null
    if (parentLabel) trail.push({ label: parentLabel })
    if (hasPage) trail.push({ label: pageLabel as string, current: true })
    return (
      <nav aria-label="Breadcrumb" className="flex items-center gap-0.5 text-[11px] leading-tight text-text-tertiary mb-2">
        {trail.map((c, i) => (
          <div key={i} className="flex items-center gap-0.5 min-w-0">
            {i > 0 && <ChevronRight size={10} className="text-text-tertiary/60 flex-shrink-0" />}
            {c.onClick ? (
              <button onClick={c.onClick} className="hover:text-brand-600 transition-colors truncate max-w-[160px] px-0.5" title={c.label}>
                {c.label}
              </button>
            ) : (
              <span className={cn('truncate max-w-[200px] px-0.5', c.current && 'text-text-primary font-medium')} title={c.label}>
                {c.label}
              </span>
            )}
          </div>
        ))}
      </nav>
    )
  }

  // No portfolio = no trail. Same for ContextSelector route.
  if (!portfolio || location.pathname === '/' || location.pathname === '/dashboard') return null

  // Always start with portfolio. Clicking the portfolio crumb must also flip
  // managementContext from 'project' (or whatever) back to 'portfolio' so the
  // sidebar nav switches with you — otherwise you land on the Portfolio
  // Dashboard with the project sidebar still showing.
  crumbs.push({
    label: portfolio.name ?? 'Portfolio',
    onClick: () => {
      setManagementContext('portfolio')
      navigate('/dashboard')
    },
  })

  // Project context: insert project crumb
  const inProject = ctx === 'project' && project
  if (inProject) {
    // Find current page name from PROJECT_PAGE_NAMES (longest-prefix match)
    let currentPage: string | undefined
    let currentMatchedPrefix = ''
    for (const [prefix, name] of Object.entries(PROJECT_PAGE_NAMES)) {
      if (path === prefix || path.startsWith(prefix + '/')) {
        if (prefix.length > currentMatchedPrefix.length) {
          currentMatchedPrefix = prefix
          currentPage = name
        }
      }
    }
    // E&C projects relabel /finance from "Baseline Management" → "Budget Management".
    if (currentMatchedPrefix === '/finance') {
      currentPage = getFinancePageLabel(
        (project as { projectType?: string | null } | null)?.projectType ?? null
      )
    }

    // If we're on a sub-page, the project crumb is clickable. Stays in
    // 'project' context — the user is still inside the same project, just
    // bouncing back from a sub-page to the project overview.
    crumbs.push({
      label: project.name,
      onClick: currentPage
        ? () => {
            setManagementContext('project')
            navigate(`/projects/${project.id}`)
          }
        : undefined,
      current: !currentPage,
    })

    if (currentPage) {
      crumbs.push({ label: currentPage, current: true })
    }
  }
  else {
    // Non-project context (portfolio): show current portfolio page if recognized
    let currentPage: string | undefined
    let currentMatchedPrefix = ''
    for (const [prefix, name] of Object.entries(PORTFOLIO_PAGE_NAMES)) {
      if (path === prefix || path.startsWith(prefix + '/')) {
        if (prefix.length > currentMatchedPrefix.length) {
          currentMatchedPrefix = prefix
          currentPage = name
        }
      }
    }

    if (currentPage) {
      crumbs.push({ label: currentPage, current: true })
    }
    else {
      // Path doesn't match any known portfolio page — skip the trail
      return null
    }
  }

  // Single crumb = just the portfolio name = no useful trail
  if (crumbs.length < 2) return null

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-0.5 text-[11px] leading-tight text-text-tertiary mb-2">
      {crumbs.map((c, i) => (
        <div key={i} className="flex items-center gap-0.5 min-w-0">
          {i > 0 && <ChevronRight size={10} className="text-text-tertiary/60 flex-shrink-0" />}
          {c.onClick ? (
            <button
              onClick={c.onClick}
              className="hover:text-brand-600 transition-colors truncate max-w-[140px] px-0.5"
              title={c.label}
            >
              {c.label}
            </button>
          ) : (
            <span
              className={cn('truncate max-w-[200px] px-0.5', c.current && 'text-text-primary font-medium')}
              title={c.label}
            >
              {c.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  )
}
